import * as React from 'react';

import {
  CurrencyCode,
  Money as MoneyType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  formatMoney,
  Loading,
  Money,
  MoneyField,
  Row,
  Select,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  OpenInvoiceForPayment,
  openInvoicesForPaymentAtom,
} from '../../_graphql/queryOpenInvoicesForPayment';
import { $applyPaymentToInvoice } from '../../_state/paymentDetail.state';

import { useTranslations } from '@/finance/i18n/useTranslations';

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  paymentId: string;
  unappliedAmount?: MoneyType;
  accountId?: string;
  currencyCode?: CurrencyCode;
  onSuccess?: () => void;
};

//
// ⚛️ ⚛️ ⚛️ PAYMENT APPLY TO INVOICE DIALOG ⚛️ ⚛️ ⚛️
//
export const PaymentApplyToInvoiceDialog: React.FC<Props> = ({
  isOpen,
  onOpenChange,
  paymentId,
  unappliedAmount,
  accountId,
  currencyCode,
  onSuccess,
}) => {
  // Translations
  const t = useTranslations();

  // States
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<
    string | undefined
  >(undefined);
  const [amount, setAmount] = React.useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Atoms
  const invoicesAtom = React.useMemo(
    () =>
      openInvoicesForPaymentAtom((_get) => ({
        variables: {
          accountId,
          currencyCode,
        },
        skip: !isOpen,
      })),
    [accountId, currencyCode, isOpen],
  );
  const {
    data: { invoices } = { invoices: [] },
    isPending: isLoadingInvoices,
  } = useAtomValue(invoicesAtom);
  const applyToInvoice = useSetAtom($applyPaymentToInvoice);

  // Data hooks
  const toast = useToast();

  // Memoized values
  const selectedInvoice = React.useMemo(
    () => invoices.find((inv) => inv.id === selectedInvoiceId),
    [invoices, selectedInvoiceId],
  );

  const maxAmount = React.useMemo(() => {
    if (!selectedInvoice || !unappliedAmount) return undefined;
    return Math.min(selectedInvoice.openAmount.amount, unappliedAmount.amount);
  }, [selectedInvoice, unappliedAmount]);

  const invoiceOptions = React.useMemo(
    () =>
      invoices.map((invoice: OpenInvoiceForPayment) => ({
        key: invoice.id,
        label: `${invoice.documentNumber} - ${invoice.account?.name ?? t.payments.detail.dialogs.applyToInvoice.unknownAccount} - ${formatMoney(invoice.openAmount.amount, invoice.openAmount.currencyCode)} ${t.payments.detail.dialogs.applyToInvoice.openSuffix}`,
      })),
    [invoices, t],
  );

  const canSubmit = selectedInvoiceId && amount != null && amount > 0;

  // Callbacks
  const handleClose = React.useCallback(() => {
    setSelectedInvoiceId(undefined);
    setAmount(undefined);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleInvoiceChange = React.useCallback(
    (key: React.Key) => {
      const invoiceId = key as string;
      setSelectedInvoiceId(invoiceId);
      const invoice = invoices.find((inv) => inv.id === invoiceId);
      if (invoice) {
        const suggestedAmount = unappliedAmount
          ? Math.min(invoice.openAmount.amount, unappliedAmount.amount)
          : invoice.openAmount.amount;
        setAmount(suggestedAmount);
      }
    },
    [invoices, unappliedAmount],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!selectedInvoiceId || amount == null || !unappliedAmount) return;

    setIsSubmitting(true);
    try {
      await applyToInvoice({
        paymentId,
        invoiceId: selectedInvoiceId,
        appliedAmount: {
          amount,
          currencyCode: unappliedAmount.currencyCode,
        },
      });
      toast.success(t.payments.detail.messages.paymentApplied);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.payments.detail.messages.applyError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedInvoiceId,
    amount,
    paymentId,
    unappliedAmount,
    applyToInvoice,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@PaymentApplyToInvoiceDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle>
            {t.payments.detail.dialogs.applyToInvoice.title}
          </DialogTitle>
        </DialogHeader>
        <DialogMain>
          <Col gap4>
            <Loading isLoading={isLoadingInvoices}>
              {invoiceOptions.length === 0 && (
                <p className="py-4 text-center text-sm text-neutral/60">
                  {t.payments.detail.dialogs.applyToInvoice.noInvoicesAvailable}
                </p>
              )}
              {invoiceOptions.length > 0 && (
                <>
                  <Select
                    label={
                      t.payments.detail.dialogs.applyToInvoice.invoiceLabel
                    }
                    value={selectedInvoiceId}
                    onChange={handleInvoiceChange}
                    options={invoiceOptions}
                    placeholder={
                      t.payments.detail.dialogs.applyToInvoice.selectInvoice
                    }
                  />
                  <MoneyField
                    label={t.payments.detail.dialogs.applyToInvoice.amountLabel}
                    amount={amount ?? 0}
                    onAmountChange={setAmount}
                    currencyCode={unappliedAmount?.currencyCode ?? 'USD'}
                    currencyLocked
                    minValue={0}
                    maxValue={maxAmount}
                  />
                  <Col gap1 className="text-sm text-neutral/60">
                    {unappliedAmount && (
                      <p>
                        {
                          t.payments.detail.dialogs.applyToInvoice
                            .unappliedBalance
                        }
                        :{' '}
                        <Money
                          amount={unappliedAmount.amount}
                          currencyCode={unappliedAmount.currencyCode}
                        />
                      </p>
                    )}
                    {selectedInvoice && (
                      <>
                        <p>
                          {
                            t.payments.detail.dialogs.applyToInvoice
                              .invoiceAmount
                          }
                          :{' '}
                          <Money
                            amount={selectedInvoice.amount.amount}
                            currencyCode={selectedInvoice.amount.currencyCode}
                          />
                        </p>
                        <p>
                          {t.payments.detail.dialogs.applyToInvoice.openBalance}
                          :{' '}
                          <Money
                            amount={selectedInvoice.openAmount.amount}
                            currencyCode={
                              selectedInvoice.openAmount.currencyCode
                            }
                          />
                        </p>
                      </>
                    )}
                  </Col>
                </>
              )}
            </Loading>
          </Col>
        </DialogMain>
        <DialogFooter>
          <Row xEnd gap3>
            <Button
              variant="outline"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              {t.payments.advances.apply.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isDisabled={!canSubmit || invoiceOptions.length === 0}
              isPending={isSubmitting}
            >
              {t.payments.advances.apply.buttons.apply}
            </Button>
          </Row>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
